import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  FiActivity,
  FiAlertTriangle,
  FiBarChart2,
  FiCheckCircle,
  FiChevronRight,
  FiClock,
  FiCpu,
  FiDollarSign,
  FiHash,
  FiInbox,
  FiLayers,
  FiMessageCircle,
  FiMessageSquare,

  FiPieChart,
  FiRefreshCw,
  FiSearch,
  FiServer,
  FiSmile,
  FiStar,
  FiTarget,
  FiThumbsDown,
  FiThumbsUp,
  FiTrendingUp,
  FiType,
  FiUsers,
  FiXCircle,
  FiZap
} from 'react-icons/fi';
import authService from '../utils/auth.js';
import { getApiBaseUrl } from '../utils/apiBase';
import { useTheme } from '../utils/theme.js';
import UserAnalyticsPanel from './UserAnalyticsPanel';
import ThemeToggle from './ThemeToggle.jsx';
import TourMode from './tour/TourMode.jsx';
import TourButton from './tour/TourButton.jsx';
import HelpMenu, { FeedbackButton } from './help/HelpMenu.jsx';
import { usePageTour } from './tour/usePageTour.js';
import '../styles/metrics-dashboard.css';
import '../styles/admin-dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

const METRIC_TABS = [
  { id: 'overview', label: 'Overview', icon: FiActivity },
  { id: 'performance', label: 'Performance', icon: FiZap },
  { id: 'cost', label: 'Cost', icon: FiDollarSign },
  { id: 'quality', label: 'Quality', icon: FiTarget },
  { id: 'usage', label: 'Usage', icon: FiBarChart2 },
  { id: 'users', label: 'Users analytics', icon: FiUsers }
];

// Guided tour of the metrics dashboard. `target` matches a data-tour attribute.
const METRICS_TOUR_STEPS = [
  { target: 'metrics-header', title: 'Your metrics hub', body: 'Live NovaBotX™ performance, quality, cost and usage in one place. The numbers refresh on their own every 30 seconds.' },
  { target: 'metrics-tabs', title: 'Switch views', body: 'Jump between Overview, Performance, Cost, Quality, Usage and per-user analytics.' },
  { target: 'metrics-kpis', title: 'Key numbers at a glance', body: 'Each card is one KPI: request volume, success rate, response time, cost, satisfaction and uptime.' },
  { target: 'metrics-chart', title: 'Request trends', body: 'Requests per hour over the last 24 hours. Hover the line to see exact counts.' },
  { target: 'metrics-refresh', title: 'Refresh on demand', body: 'Pull the latest numbers right now instead of waiting for the next automatic refresh.' }
];

const REDUCED_MOTION =
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Animates the numeric part of a pre-formatted value ("$1.2K", "98.5%", "340.00ms").
function AnimatedValue({ text }) {
  const str = String(text);
  const match = str.match(/^([^\d-]*)(-?\d+(?:\.\d+)?)(.*)$/);
  const target = match ? parseFloat(match[2]) : null;
  const decimals = match && match[2].includes('.') ? match[2].split('.')[1].length : 0;
  const fromRef = useRef(0);
  const [shown, setShown] = useState(REDUCED_MOTION || target === null ? target : 0);

  useEffect(() => {
    if (target === null) return undefined;
    if (REDUCED_MOTION) {
      setShown(target);
      return undefined;
    }
    const from = fromRef.current;
    const start = performance.now();
    const duration = 750;
    let raf = 0;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(from + (target - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      fromRef.current = target;
    };
  }, [target]);

  if (!match) return <>{str}</>;
  return <>{match[1]}{Number(shown ?? 0).toFixed(decimals)}{match[3]}</>;
}

function StatCard({ icon: Icon, title, value, label, tone = 'blue', valueClass = '', small = false }) {
  return (
    <div className={`metric-card tone-${tone}`}>
      <div className="metric-card-head">
        <span className="metric-icon" aria-hidden="true"><Icon /></span>
        <h3>{title}</h3>
      </div>
      <div className={`metric-value ${valueClass} ${small ? 'is-text' : ''}`.trim()}>
        {small ? value : <AnimatedValue text={value} />}
      </div>
      <div className="metric-label">{label}</div>
    </div>
  );
}

function SectionTitle({ icon: Icon, children, caption }) {
  return (
    <div className="metrics-section-head">
      <span className="metrics-section-icon" aria-hidden="true"><Icon /></span>
      <div>
        <h3>{children}</h3>
        {caption && <p>{caption}</p>}
      </div>
    </div>
  );
}

// Read the design tokens so chart colours follow light/dark mode.
function readChartTokens() {
  const css = getComputedStyle(document.documentElement);
  const v = (name, fallback) => css.getPropertyValue(name).trim() || fallback;
  return {
    blue: v('--nb-blue', '#0b8cd6'),
    green: v('--nb-green', '#4cb648'),
    teal: v('--nb-teal', '#19a7a0'),
    warning: v('--nb-warning', '#d97706'),
    danger: v('--nb-danger', '#d9404b'),
    blue200: v('--nb-blue-200', '#9fd3f0'),
    ink: v('--nb-ink', '#0b1526'),
    ink2: v('--nb-ink-2', '#334155'),
    muted: v('--nb-muted', '#64748b'),
    line: v('--nb-line', '#e4e9f0'),
    surface: v('--nb-surface', '#ffffff'),
    panel: v('--nb-panel', '#0b1526'),
    panelInk: v('--nb-panel-ink', '#cbd5e1'),
    font: v('--nb-font', 'Inter, system-ui, sans-serif')
  };
}

function withAlpha(color, alpha) {
  const hex = color.replace('#', '');
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    const n = parseInt(hex, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }
  return color;
}

function MetricsDashboard({ isSuperAdmin = authService.isSuperAdmin(), selectedTenant = null }) {
  console.log('=== MetricsDashboard component STARTING ===');
  console.log('MetricsDashboard component rendering...');
  const [metricsData, setMetricsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);
  const refreshIntervalRef = useRef(null);
  const API_BASE_URL = getApiBaseUrl();
  const [theme] = useTheme();
  // Rendered on its own at /metrics, or embedded as a tab inside the admin dashboard.
  const isStandalone = typeof window !== 'undefined' && /\/metrics\/?$/.test(window.location.pathname);
  const { touring, start: startTour, end: endTour } = usePageTour();

  console.log('MetricsDashboard state - loading:', loading, 'error:', error, 'hasData:', !!metricsData);
  console.log('API_BASE_URL calculated:', API_BASE_URL);
  console.log('Current pathname:', typeof window !== 'undefined' ? window.location.pathname : 'N/A');

  useEffect(() => {
    console.log('=== MetricsDashboard useEffect STARTING ===');
    console.log('MetricsDashboard mounted, API_BASE_URL:', API_BASE_URL);
    console.log('Current pathname:', typeof window !== 'undefined' ? window.location.pathname : 'N/A');
    console.log('About to call fetchMetrics...');
    fetchMetrics();

    // Auto-refresh every 30 seconds
    refreshIntervalRef.current = setInterval(fetchMetrics, 30000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  const fetchMetrics = async () => {
    const apiUrl = `${API_BASE_URL}/api/metrics/dashboard`;
    console.log('=== fetchMetrics CALLED ===');
    console.log('API URL:', apiUrl);
    console.log('API_BASE_URL:', API_BASE_URL);
    console.log('Auth headers:', authService.getAuthHeaders());

    try {
      console.log('Making fetch request to:', apiUrl);
      const response = await fetch(apiUrl, {
        headers: authService.getAuthHeaders()
      });

      console.log('Fetch response received:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Metrics data received:', data);

      if (data.success) {
        setMetricsData(data.dashboard);
        setError(null);
        console.log('Metrics data set successfully');
      } else {
        throw new Error(data.message || 'Failed to fetch metrics');
      }
    } catch (err) {
      console.error('Error fetching metrics:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        apiUrl: apiUrl
      });
      setError('Failed to fetch metrics: ' + err.message);
    } finally {
      setLoading(false);
      console.log('fetchMetrics completed, loading set to false');
    }
  };

  // Manual refresh: same fetch, plus a spinning icon while it runs.
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchMetrics();
    } finally {
      setRefreshing(false);
    }
  };

  // The tour points at the Overview tab's cards and chart.
  useEffect(() => {
    if (touring) setActiveSubTab('overview');
  }, [touring]);

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toFixed(2) || '0';
  };

  // Whole-number counts (requests, tokens): no decimals, compact above 1,000.
  const formatCount = (num) => {
    const n = Math.round(num || 0);
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return String(n);
  };

  // Durations given in milliseconds: "850 ms" below a second, "4.4 s" above.
  const formatMs = (ms) => {
    const n = Number(ms) || 0;
    return n >= 1000 ? `${(n / 1000).toFixed(1)} s` : `${Math.round(n)} ms`;
  };

  const formatPercentage = (num) => {
    // Backend returns percentages as 0-100 (successRate, errorRate)
    return (num || 0).toFixed(1) + '%';
  };

  const formatDecimalAsPercentage = (num) => {
    // Backend returns some metrics as decimals 0-1 (accuracy, precision, recall)
    return ((num || 0) * 100).toFixed(1) + '%';
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tokens = useMemo(() => readChartTokens(), [theme]);

  // Prepare chart data
  const requestsData = metricsData?.trends?.requestsPerHour || {};
  const requestsChartData = {
    labels: Object.keys(requestsData).map(h => h + ':00'),
    datasets: [{
      label: 'Requests',
      data: Object.values(requestsData),
      borderColor: tokens.blue,
      borderWidth: 2.5,
      backgroundColor: (ctx) => {
        const { chart } = ctx;
        const area = chart.chartArea;
        if (!area) return withAlpha(tokens.blue, 0.12);
        const g = chart.ctx.createLinearGradient(0, area.top, 0, area.bottom);
        g.addColorStop(0, withAlpha(tokens.blue, 0.28));
        g.addColorStop(1, withAlpha(tokens.blue, 0));
        return g;
      },
      pointBackgroundColor: tokens.surface,
      pointBorderColor: tokens.blue,
      pointBorderWidth: 2,
      pointRadius: 3,
      pointHoverRadius: 6,
      pointHoverBackgroundColor: tokens.green,
      tension: 0.4,
      fill: true
    }]
  };

  const topicData = metricsData?.trends?.topicDistribution || {};
  const topicChartData = {
    labels: Object.keys(topicData),
    datasets: [{
      data: Object.values(topicData),
      backgroundColor: [
        tokens.blue,
        tokens.green,
        tokens.teal,
        tokens.warning,
        tokens.blue200,
        tokens.danger,
        tokens.muted
      ],
      borderColor: tokens.surface,
      borderWidth: 3,
      hoverOffset: 6
    }]
  };

  const tooltip = {
    backgroundColor: tokens.panel,
    titleColor: '#ffffff',
    bodyColor: tokens.panelInk,
    borderColor: withAlpha(tokens.blue, 0.4),
    borderWidth: 1,
    padding: 10,
    cornerRadius: 10,
    displayColors: false,
    titleFont: { family: tokens.font, weight: '600' },
    bodyFont: { family: tokens.font }
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    animation: REDUCED_MOTION ? false : { duration: 700, easing: 'easeOutCubic' },
    plugins: {
      legend: { display: false },
      tooltip
    },
    scales: {
      x: {
        ticks: { color: tokens.muted, font: { family: tokens.font, size: 11 }, maxRotation: 0, autoSkipPadding: 12 },
        grid: { display: false },
        border: { color: tokens.line }
      },
      y: {
        beginAtZero: true,
        ticks: { color: tokens.muted, font: { family: tokens.font, size: 11 }, precision: 0 },
        grid: { color: tokens.line },
        border: { display: false }
      }
    }
  };

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '66%',
    animation: REDUCED_MOTION ? false : { duration: 700, easing: 'easeOutCubic' },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: tokens.ink2,
          padding: 16,
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          font: { family: tokens.font, size: 12 }
        }
      },
      tooltip: { ...tooltip, displayColors: true }
    }
  };

  const hasData = !loading && !error && !!metricsData;
  const hasTrendChart = hasData && activeSubTab === 'overview' && Object.keys(requestsData).length > 0;
  const tourSteps = METRICS_TOUR_STEPS.filter((s) => {
    if (s.target === 'metrics-tabs' || s.target === 'metrics-kpis') return hasData;
    if (s.target === 'metrics-chart') return hasTrendChart;
    return true;
  });

  const timing = metricsData?.performance?.timingBreakdown;
  const timingTotal = timing?.totalProcessing?.average || 0;
  const timingShare = (key) =>
    timingTotal > 0 ? ((timing?.[key]?.average || 0) / timingTotal * 100) : 0;

  // Always render something immediately - no blank page
  console.log('MetricsDashboard: About to return JSX');

  return (
    <div className={`metrics-page ${isStandalone ? 'is-standalone' : 'is-embedded'}`}>
      <main className="metrics-shell">
        <header className="metrics-header" data-tour="metrics-header">
          <div className="metrics-header-copy">
            <span className="metrics-kicker">
              <span className={`metrics-live-dot ${error ? 'is-off' : ''}`} aria-hidden="true" />
              {error ? 'Connection issue' : 'Live analytics'}
            </span>
            <h2 className="metrics-title">Metrics overview</h2>
            <p className="metrics-panel-subtitle">Live NovaBotX™ performance, quality, cost, and usage.</p>
          </div>
          <div className="metrics-header-actions">
            <span className="metrics-auto-chip" title="Metrics refresh automatically">
              <FiClock aria-hidden="true" /> Auto-refresh 30s
            </span>
            <button
              type="button"
              className={`metrics-refresh-btn ${refreshing ? 'is-busy' : ''}`}
              onClick={handleRefresh}
              disabled={refreshing}
              data-tour="metrics-refresh"
            >
              <FiRefreshCw aria-hidden="true" />
              <span>{refreshing ? 'Refreshing…' : 'Refresh'}</span>
            </button>
            {/* Inside the admin console its header already has Tour and the theme switch */}
            {isStandalone && <HelpMenu onTour={startTour} />}
            {isStandalone && <FeedbackButton />}
            {isStandalone && <TourButton onClick={startTour} />}
            {isStandalone && <ThemeToggle compact />}
          </div>
        </header>

        {/* Loading State */}
        {loading && (
          <div className="metrics-loading" aria-busy="true" aria-label="Loading metrics">
            <div className="metrics-skeleton-tabs nb-skeleton" />
            <div className="metrics-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="metrics-skeleton-card">
                  <div className="nb-skeleton metrics-skel-icon" />
                  <div className="nb-skeleton metrics-skel-line" />
                  <div className="nb-skeleton metrics-skel-value" />
                </div>
              ))}
            </div>
            <div className="nb-skeleton metrics-skel-chart" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="metrics-state metrics-state-error" role="alert">
            <span className="metrics-state-icon"><FiAlertTriangle aria-hidden="true" /></span>
            <div className="metrics-state-title">Unable to load metrics</div>
            <p>{error}</p>
            <button onClick={handleRefresh} className="metrics-action-button" disabled={refreshing}>
              <FiRefreshCw aria-hidden="true" /> Retry
            </button>
            <div className="metrics-api-url">
              API URL: {API_BASE_URL}/api/metrics/dashboard
            </div>
          </div>
        )}

        {/* No Data State */}
        {!loading && !error && !metricsData && (
          <div className="metrics-state">
            <span className="metrics-state-icon"><FiInbox aria-hidden="true" /></span>
            <div className="metrics-state-title">No metrics data available</div>
            <p>Metrics appear here once NovaBotX™ starts answering questions.</p>
            <button onClick={handleRefresh} className="metrics-action-button" disabled={refreshing}>
              <FiRefreshCw aria-hidden="true" /> Refresh
            </button>
          </div>
        )}

        {/* Metrics Dashboard Content */}
        {hasData && (
          <div className="metrics-dashboard-panel">
      {/* Sub-tabs */}
      <div className="metrics-tabs" role="tablist" aria-label="Metrics views" data-tour="metrics-tabs">
        {METRIC_TABS.map((t) => {
          const Icon = t.icon;
          const active = activeSubTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`metrics-tab ${active ? 'active' : ''}`}
              onClick={() => setActiveSubTab(t.id)}
            >
              <Icon aria-hidden="true" />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Overview Tab */}
      {activeSubTab === 'overview' && (
        <div className="metrics-tab-content">
          <div className="metrics-grid nb-stagger" data-tour="metrics-kpis">
            <StatCard icon={FiMessageSquare} title="Total Requests" value={formatCount(metricsData.overview?.totalRequests)} label="All time requests" />
            <StatCard icon={FiCheckCircle} tone="green" title="Success Rate" valueClass="success-rate" value={formatPercentage(metricsData.overview?.successRate)} label="Successful responses" />
            <StatCard icon={FiZap} tone="teal" title="Avg Response Time" value={formatMs(metricsData.overview?.averageResponseTime)} label="Per answer" />
            <StatCard icon={FiDollarSign} tone="green" title="Total Cost" value={`$${formatNumber(metricsData.overview?.totalCost)}`} label="USD" />
            <StatCard icon={FiStar} tone="warning" title="User Satisfaction" value={`${formatNumber(metricsData.quality?.userSatisfaction)}/5`} label="Average rating" />
            <StatCard icon={FiServer} tone="blue" title="Uptime" value={`${formatNumber((metricsData.overview?.uptime || 0) / 3600000)}h`} label="Hours" />
          </div>

          {Object.keys(requestsData).length > 0 && (
            <div className="chart-container" data-tour="metrics-chart">
              <SectionTitle icon={FiTrendingUp} caption="Requests per hour">Request Trends (Last 24 Hours)</SectionTitle>
              <div className="metrics-chart-canvas">
                <Line key={`line-${theme}`} data={requestsChartData} options={lineChartOptions} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Performance Tab */}
      {activeSubTab === 'performance' && (
        <div className="metrics-tab-content">
          <div className="metrics-grid metrics-grid-4 nb-stagger">
            <StatCard icon={FiActivity} title="Requests/Min" value={formatNumber(metricsData.performance?.requestsPerMinute || 0)} label="Current load" />
            <StatCard icon={FiXCircle} tone="danger" title="Error Rate" valueClass="error-rate" value={formatPercentage(metricsData.performance?.errorRate)} label="Failed requests" />
            <StatCard icon={FiZap} tone="teal" title="Avg Response Time" value={formatMs(metricsData.performance?.averageResponseTime)} label="Per answer" />
            <StatCard icon={FiRefreshCw} tone="green" title="Active Sessions" value={String(metricsData.usage?.activeSessions || 0)} label="Current sessions" />
          </div>

          {/* Chatbot Flow Timing Breakdown */}
          {metricsData.performance?.timingBreakdown && (
            <div className="timing-breakdown-section">
              <SectionTitle icon={FiSearch} caption="Average time spent in each stage of a request">
                <span className="timing-breakdown-title">Chatbot Flow Performance Breakdown</span>
              </SectionTitle>
              <div className="timing-flow nb-stagger">
                <div className="timing-step">
                  <div className="step-icon"><FiMessageCircle aria-hidden="true" /></div>
                  <div className="step-name">Query Received</div>
                  <div className="step-time">Start</div>
                </div>
                <div className="timing-arrow" aria-hidden="true"><FiChevronRight /></div>
                <div className="timing-step">
                  <div className="step-icon"><FiLayers aria-hidden="true" /></div>
                  <div className="step-name">Embedding Generation</div>
                  <div className="step-time">
                    {formatMs(metricsData.performance.timingBreakdown.embeddingGeneration?.average)}
                  </div>
                  <div className="step-count">
                    {metricsData.performance.timingBreakdown.embeddingGeneration?.count || 0} calls
                  </div>
                </div>
                <div className="timing-arrow" aria-hidden="true"><FiChevronRight /></div>
                <div className="timing-step">
                  <div className="step-icon"><FiSearch aria-hidden="true" /></div>
                  <div className="step-name">Vector DB Search</div>
                  <div className="step-time">
                    {formatMs(metricsData.performance.timingBreakdown.vectorSearch?.average)}
                  </div>
                  <div className="step-count">
                    {metricsData.performance.timingBreakdown.vectorSearch?.count || 0} searches
                  </div>
                </div>
                <div className="timing-arrow" aria-hidden="true"><FiChevronRight /></div>
                <div className="timing-step">
                  <div className="step-icon"><FiCpu aria-hidden="true" /></div>
                  <div className="step-name">LLM Inference</div>
                  <div className="step-time">
                    {formatMs(metricsData.performance.timingBreakdown.llmInference?.average)}
                  </div>
                  <div className="step-count">
                    {metricsData.performance.timingBreakdown.llmInference?.count || 0} inferences
                  </div>
                </div>
                <div className="timing-arrow" aria-hidden="true"><FiChevronRight /></div>
                <div className="timing-step is-final">
                  <div className="step-icon"><FiCheckCircle aria-hidden="true" /></div>
                  <div className="step-name">Response Served</div>
                  <div className="step-time">
                    {formatMs(metricsData.performance.timingBreakdown.totalProcessing?.average)}
                  </div>
                  <div className="step-label">Total Time</div>
                </div>
              </div>

              {/* Share of total time, as one stacked bar */}
              {timingTotal > 0 && (
                <div className="timing-share" aria-hidden="true">
                  <span className="seg seg-embed" style={{ width: `${timingShare('embeddingGeneration')}%` }} />
                  <span className="seg seg-vector" style={{ width: `${timingShare('vectorSearch')}%` }} />
                  <span className="seg seg-llm" style={{ width: `${timingShare('llmInference')}%` }} />
                </div>
              )}

              {/* Timing Breakdown Stats */}
              <div className="timing-stats">
                <div className="timing-stat-card">
                  <div className="timing-stat-label"><i className="dot seg-embed" />Embedding % of Total</div>
                  <div className="timing-stat-value">
                    {metricsData.performance.timingBreakdown.totalProcessing?.average > 0
                      ? ((metricsData.performance.timingBreakdown.embeddingGeneration?.average || 0) /
                         metricsData.performance.timingBreakdown.totalProcessing?.average * 100).toFixed(1)
                      : '0'}%
                  </div>
                </div>
                <div className="timing-stat-card">
                  <div className="timing-stat-label"><i className="dot seg-vector" />Vector Search % of Total</div>
                  <div className="timing-stat-value">
                    {metricsData.performance.timingBreakdown.totalProcessing?.average > 0
                      ? ((metricsData.performance.timingBreakdown.vectorSearch?.average || 0) /
                         metricsData.performance.timingBreakdown.totalProcessing?.average * 100).toFixed(1)
                      : '0'}%
                  </div>
                </div>
                <div className="timing-stat-card">
                  <div className="timing-stat-label"><i className="dot seg-llm" />LLM % of Total</div>
                  <div className="timing-stat-value">
                    {metricsData.performance.timingBreakdown.totalProcessing?.average > 0
                      ? ((metricsData.performance.timingBreakdown.llmInference?.average || 0) /
                         metricsData.performance.timingBreakdown.totalProcessing?.average * 100).toFixed(1)
                      : '0'}%
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cost Tab */}
      {activeSubTab === 'cost' && (
        <div className="metrics-tab-content">
          <div className="metrics-grid metrics-grid-4 nb-stagger">
            <StatCard icon={FiDollarSign} tone="green" title="Cost/Request" value={`$${formatNumber(metricsData.cost?.costPerRequest || 0)}`} label="Average cost" />
            <StatCard icon={FiTarget} tone="blue" title="Cost/Session" value={`$${formatNumber(metricsData.cost?.costPerSession || 0)}`} label="Per session" />
            <StatCard icon={FiType} tone="teal" title="Total Tokens" value={formatCount(metricsData.cost?.totalTokens)} label="All time usage" />
            <StatCard icon={FiHash} tone="blue" title="Avg Tokens/Request" value={formatCount(metricsData.cost?.averageTokensPerRequest)} label="Token efficiency" />
          </div>
        </div>
      )}

      {/* Quality Tab */}
      {activeSubTab === 'quality' && (
        <div className="metrics-tab-content">
          <div className="metrics-grid metrics-grid-4 nb-stagger">
            <StatCard icon={FiTarget} tone="green" title="Accuracy" value={formatDecimalAsPercentage(metricsData.quality?.accuracy)} label="Response accuracy" />
            <StatCard icon={FiBarChart2} tone="blue" title="F1 Score" value={formatNumber(metricsData.quality?.f1Score || 0)} label="Quality metric" />
            <StatCard icon={FiSearch} tone="teal" title="Precision" value={formatDecimalAsPercentage(metricsData.quality?.precision)} label="True positive rate" />
            <StatCard icon={FiTrendingUp} tone="blue" title="Recall" value={formatDecimalAsPercentage(metricsData.quality?.recall)} label="Detection rate" />
          </div>

          {/* Vote Feedback Summary in Quality Tab */}
          {metricsData.quality?.voteFeedback && (
            <div className="vote-feedback-summary">
              <SectionTitle icon={FiThumbsUp} caption="Thumbs up / down on answers">Vote Feedback</SectionTitle>
              <div className="metrics-grid metrics-grid-4 nb-stagger">
                <StatCard icon={FiBarChart2} title="Total Votes" value={String(metricsData.quality.voteFeedback.totalVotes || 0)} label="User interactions" />
                <StatCard icon={FiThumbsUp} tone="green" title="Upvotes" valueClass="success-rate" value={String(metricsData.quality.voteFeedback.upVotes || 0)} label="Positive feedback" />
                <StatCard icon={FiThumbsDown} tone="danger" title="Downvotes" valueClass="error-rate" value={String(metricsData.quality.voteFeedback.downVotes || 0)} label="Negative feedback" />
                <StatCard icon={FiSmile} tone="warning" title="Satisfaction" value={formatPercentage(metricsData.quality.voteFeedback.satisfactionFromVotes || 0)} label="From votes" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Usage Tab */}
      {activeSubTab === 'usage' && (
        <div className="metrics-tab-content">
          <div className="metrics-grid metrics-grid-4 nb-stagger">
            <StatCard icon={FiUsers} title="Total Sessions" value={String(metricsData.usage?.totalSessions || 0)} label="All time sessions" />
            <StatCard icon={FiMessageCircle} tone="green" title="Avg Messages/Session" value={formatNumber(metricsData.usage?.averageMessagesPerSession || 0)} label="Engagement" />
            <StatCard icon={FiClock} tone="teal" title="Avg Session Length" value={`${formatNumber((metricsData.usage?.averageSessionLength || 0) / 60000)}m`} label="Minutes" />
            <StatCard
              icon={FiPieChart}
              tone="warning"
              title="Top Topic"
              small
              value={Object.keys(topicData).length > 0
                ? Object.keys(topicData).reduce((a, b) => topicData[a] > topicData[b] ? a : b, 'N/A')
                : 'N/A'}
              label="Most popular"
            />
          </div>

          {Object.keys(topicData).length > 0 && (
            <div className="chart-container">
              <SectionTitle icon={FiPieChart} caption="Share of questions by topic">Topic Distribution</SectionTitle>
              <div className="metrics-chart-canvas is-doughnut">
                <Doughnut key={`doughnut-${theme}`} data={topicChartData} options={doughnutChartOptions} />
              </div>
            </div>
          )}
        </div>
      )}

      {activeSubTab === 'users' && (
        <div className="metrics-tab-content">
          <UserAnalyticsPanel
            apiBaseUrl={API_BASE_URL}
            isSuperAdmin={isSuperAdmin}
            selectedTenant={selectedTenant}
            embedded
          />
        </div>
      )}
          </div>
        )}
      </main>

      {touring && !loading && <TourMode key={tourSteps.length} steps={tourSteps} topOffset={8} onClose={endTour} />}
    </div>
  );
}

export default MetricsDashboard;
